import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Plus, Save, Trash2, Sparkles } from 'lucide-react';
import api from '../../services/api';
import { field, ghost, primary, muted, card, rowBorder, chip, errorText } from '../time/styles';

// Lifecycle setup → Letters: templates HR writes with {{fields}}, previewed
// live on the organisation's letterhead.

const blank = { kind: 'OFFER', name: '', subject: '', body: '', requiresSignature: false, isActive: true };
const MARKUP_HELP = 'Write plain text. A blank line starts a new paragraph. "# " starts a heading, "- " a list item, **text** is bold.';

/** The letter page, rendered by the server, in a sandboxed frame. */
export const LetterFrame = ({ html, title }) => (
    <iframe title={title} sandbox="" srcDoc={html || ''} style={{ width: '100%', height: 560, border: rowBorder, borderRadius: 6, background: '#f3f4f6' }} />
);

const Editor = ({ initial, meta, users, onSaved, onDeleted, say }) => {
    const [t, setT] = useState(initial);
    const [saving, setSaving] = useState(false);
    const [previewFor, setPreviewFor] = useState('');
    const [preview, setPreview] = useState(null);
    const bodyRef = useRef(null);
    const set = (k, v) => setT(prev => ({ ...prev, [k]: v }));

    // Live preview, a moment after typing stops; a slower answer for older text is ignored.
    useEffect(() => {
        if (t.body.trim().length < 10) { setPreview(null); return undefined; }
        let live = true;
        const timer = setTimeout(() => {
            api.post('/lifecycle/templates/preview', { kind: t.kind, name: t.name || 'Preview', subject: t.subject, body: t.body, userId: previewFor || undefined })
                .then(r => { if (live) setPreview(r.data); })
                .catch(e => { if (live) setPreview({ error: errorText(e, 'Preview failed') }); });
        }, 400);
        return () => { live = false; clearTimeout(timer); };
    }, [t.kind, t.name, t.subject, t.body, previewFor]);

    const insert = (key) => {
        const el = bodyRef.current;
        const token = `{{${key}}}`;
        if (!el) { set('body', `${t.body}${token}`); return; }
        const { selectionStart: a, selectionEnd: b } = el;
        const next = t.body.slice(0, a) + token + t.body.slice(b);
        set('body', next);
        requestAnimationFrame(() => { el.focus(); el.setSelectionRange(a + token.length, a + token.length); });
    };

    const save = async () => {
        setSaving(true);
        try {
            const body = { kind: t.kind, name: t.name, subject: t.subject, body: t.body, requiresSignature: t.requiresSignature, isActive: t.isActive };
            const r = t.id ? await api.put(`/lifecycle/templates/${t.id}`, body) : await api.post('/lifecycle/templates', body);
            setT(r.data);
            onSaved(r.data);
            say(true, `Saved "${r.data.name}"`);
        } catch (e) {
            say(false, errorText(e, 'Could not save the template'));
        } finally {
            setSaving(false);
        }
    };

    const remove = async () => {
        if (!window.confirm(`Delete the template "${t.name}"? Letters already issued from it are kept.`)) return;
        try {
            await api.delete(`/lifecycle/templates/${t.id}`);
            onDeleted(t.id);
            say(true, `Deleted "${t.name}"`);
        } catch (e) {
            say(false, errorText(e, 'Could not delete the template'));
        }
    };

    return (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: 20 }}>
            <div style={{ display: 'grid', gap: 12, alignContent: 'start' }}>
                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                    <label style={{ display: 'grid', gap: 4, flex: '1 1 180px' }}><span style={muted}>Template name</span>
                        <input style={field} value={t.name} onChange={e => set('name', e.target.value)} maxLength={100} />
                    </label>
                    <label style={{ display: 'grid', gap: 4 }}><span style={muted}>Kind</span>
                        <select style={field} value={t.kind} onChange={e => set('kind', e.target.value)}>
                            {meta.kinds.map(k => <option key={k} value={k}>{meta.kindLabels[k]}</option>)}
                        </select>
                    </label>
                </div>
                <label style={{ display: 'grid', gap: 4 }}><span style={muted}>Subject line (optional)</span>
                    <input style={field} value={t.subject} onChange={e => set('subject', e.target.value)} maxLength={200} />
                </label>
                <label style={{ display: 'grid', gap: 4 }}><span style={muted}>Letter text</span>
                    <textarea ref={bodyRef} aria-label="Letter text" style={{ ...field, minHeight: 300, fontFamily: 'ui-monospace, monospace', fontSize: 13, lineHeight: 1.5 }} value={t.body} onChange={e => set('body', e.target.value)} />
                </label>
                <p style={{ ...muted, margin: 0 }}>{MARKUP_HELP}</p>
                <div>
                    <span style={muted}>Insert a field at the cursor:</span>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 6 }}>
                        {meta.mergeFields.map(f => (
                            <button key={f.key} type="button" title={`{{${f.key}}}`} onClick={() => insert(f.key)} style={{ ...chip, cursor: 'pointer', background: 'transparent' }}>{f.label}</button>
                        ))}
                    </div>
                    <p style={{ ...muted, margin: '6px 0 0' }}>For a value you type when issuing the letter, write <code>{'{{extra.anyName}}'}</code>, for example <code>{'{{extra.offerValidUntil}}'}</code>.</p>
                </div>
                <label style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <input type="checkbox" checked={t.requiresSignature} onChange={e => set('requiresSignature', e.target.checked)} />
                    <span>The employee signs this letter in the app</span>
                </label>
                <label style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <input type="checkbox" checked={t.isActive} onChange={e => set('isActive', e.target.checked)} />
                    <span>Available when issuing letters</span>
                </label>
                <div style={{ display: 'flex', gap: 8 }}>
                    <button className="btn btn-primary" style={primary} onClick={save} disabled={saving}><Save size={14} /> {saving ? 'Saving…' : 'Save template'}</button>
                    {t.id && <button style={ghost} onClick={remove}><Trash2 size={14} /> Delete</button>}
                </div>
            </div>
            <div style={{ display: 'grid', gap: 8, alignContent: 'start' }}>
                <label style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                    <span style={muted}>Preview with</span>
                    <select aria-label="Preview with" style={field} value={previewFor} onChange={e => setPreviewFor(e.target.value)}>
                        <option value="">Sample values</option>
                        {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
                    </select>
                </label>
                {preview?.error && <p role="alert" style={{ color: '#ef4444', margin: 0 }}>{preview.error}</p>}
                {preview?.missing?.length > 0 && (
                    <p style={{ ...muted, margin: 0 }}>Highlighted gaps have no value yet ({preview.missing.join(', ')}). They must be filled in before the letter can be issued.</p>
                )}
                {preview?.html ? <LetterFrame html={preview.html} title="Letter preview" /> : <p style={muted}>The preview appears here as you write.</p>}
            </div>
        </div>
    );
};

const LettersTab = ({ users, say }) => {
    const [meta, setMeta] = useState(null);
    const [editing, setEditing] = useState(null);

    const load = useCallback(() => api.get('/lifecycle/templates').then(r => setMeta(r.data)).catch(e => say(false, errorText(e, 'Could not load letter templates'))), [say]);
    useEffect(() => { load(); }, [load]);

    const addStarters = async () => {
        try {
            const r = await api.post('/lifecycle/templates/starters');
            say(true, r.data.added.length ? `Added ${r.data.added.join(', ')}` : 'All starter templates are already there');
            load();
        } catch (e) {
            say(false, errorText(e, 'Could not add the starter templates'));
        }
    };

    if (!meta) return <p style={muted}>Loading…</p>;
    if (editing) {
        return (
            <section className="card" style={card}>
                <button style={{ ...ghost, marginBottom: 16 }} onClick={() => { setEditing(null); load(); }}>← All templates</button>
                <Editor key={editing.id || 'new'} initial={editing} meta={meta} users={users} say={say}
                    onSaved={() => load()} onDeleted={() => { setEditing(null); load(); }} />
            </section>
        );
    }
    return (
        <section className="card" style={card}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10, marginBottom: 12 }}>
                <p style={{ ...muted, margin: 0, fontSize: 14 }}>Letters are printed on your letterhead (name, address and logo from Settings, in your brand colour).</p>
                <div style={{ display: 'flex', gap: 8 }}>
                    {meta.startersAvailable.length > 0 && <button style={ghost} onClick={addStarters}><Sparkles size={14} /> Add starter templates</button>}
                    <button className="btn btn-primary" style={primary} onClick={() => setEditing({ ...blank })}><Plus size={14} /> New template</button>
                </div>
            </div>
            {meta.templates.length === 0 ? (
                <p style={muted}>No templates yet. Start from the starter set (offer, appointment, relieving and experience letters) and edit them to suit.</p>
            ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14, color: 'var(--text-main)' }}>
                    <thead><tr style={{ textAlign: 'left', ...muted }}><th style={{ padding: 8 }}>Template</th><th style={{ padding: 8 }}>Kind</th><th style={{ padding: 8 }}>Signed in the app</th><th /></tr></thead>
                    <tbody>
                        {meta.templates.map(t => (
                            <tr key={t.id} style={{ borderTop: rowBorder, opacity: t.isActive ? 1 : 0.6 }}>
                                <td style={{ padding: 8 }}>{t.name}{!t.isActive && <span style={muted}> (not in use)</span>}</td>
                                <td style={{ padding: 8 }}>{meta.kindLabels[t.kind]}</td>
                                <td style={{ padding: 8 }}>{t.requiresSignature ? 'Yes' : 'No'}</td>
                                <td style={{ padding: 8, textAlign: 'right' }}><button style={ghost} aria-label={`Edit ${t.name}`} onClick={() => setEditing(t)}>Edit</button></td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}
        </section>
    );
};

export default LettersTab;
